import { MaterialCommunityIcons } from '@expo/vector-icons';
import { Asset, useUpdateMutation } from '@gripp/shared-logic';
import { Button, Colors, colors } from '@gripp/shared-ui';
import { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { Menu, Modal, Portal } from 'react-native-paper';
import { CreateFileForm } from '../../files/components/createFileForm';
import { AddButton } from '../../shared/components/addButton/addButton';
import { FilesContext } from './filesContext';

export type AddAssetMediaType = 'file' | 'link';

type Props = {
  asset: Asset;
  invalidateQuery: () => void;
  disabled?: boolean;
  useCropCircle?: boolean;
  headerMode?: boolean;
};

// TODO: Crop Circle Redesign | Remove after feature is fully rolled out
export const AddFilesLinksButton = ({ useCropCircle, ...props }: Props) => {
  // TODO: Crop Circle Redesign | Remove after feature is fully rolled out
  if (!useCropCircle) {
    return <AddFilesLinksButtonOld {...props} />;
  }

  // TODO: Crop Circle Redesign | Remove to AddFilesLinksButtonBase after feature is fully rolled out
  return <AddFilesLinksButtonBase {...props} />;
};

export const AddFilesLinksHeaderButton = ({
  disabled,
}: {
  disabled?: boolean;
}) => {
  const { setIsAddSheetOpen } = useContext(FilesContext);
  if (disabled) return null;
  return <AddButton onPress={() => setIsAddSheetOpen(true)} />;
};

type SheetProps = {
  asset: Asset;
  invalidateQuery: () => void;
  isOpen: boolean;
  onClose: () => void;
  selectedType: AddAssetMediaType | null;
  onSelectedTypeChange: (type: AddAssetMediaType | null) => void;
};

export const AddFilesLinksSheet = ({
  asset,
  invalidateQuery,
  isOpen,
  onClose,
  selectedType,
  onSelectedTypeChange,
}: SheetProps) => {
  const { t } = useTranslation();

  const mediaIdsRef = useRef<Set<string>>(
    new Set((asset?.media || []).map((m) => m.id))
  );

  useEffect(() => {
    mediaIdsRef.current = new Set((asset?.media || []).map((m) => m.id));
  }, [asset?.id, asset?.media]);

  const { mutateAsync: updateAsset } = useUpdateMutation({
    modelName: 'asset',
    query: UPDATE_ASSET,
  });

  const handleModalComplete = () => {
    onSelectedTypeChange(null);
  };

  const onAssetUploadComplete = async (id: string) => {
    mediaIdsRef.current.add(id);
    const media = Array.from(mediaIdsRef.current).map((mid) => ({ id: mid }));
    await updateAsset({ input: { id: asset.id, media } });
    await invalidateQuery();
    handleModalComplete();
  };

  const handleTypeSelect = (type: AddAssetMediaType) => {
    onSelectedTypeChange(type);
    onClose();
  };

  return (
    <>
      <Portal>
        <Modal
          visible={!!selectedType}
          onDismiss={handleModalComplete}
          contentContainerStyle={baseStyles.modalContainer}
        >
          {selectedType && (
            <CreateFileForm
              type={selectedType}
              isProductContext
              onProductUploadComplete={onAssetUploadComplete}
              onComplete={handleModalComplete}
            />
          )}
        </Modal>
      </Portal>
      {isOpen && (
        <View style={sheetStyles.overlay}>
          <Pressable
            style={sheetStyles.backdrop}
            onPress={onClose}
          />
          <View style={sheetStyles.sheet}>
            <View style={sheetStyles.headerBlock}>
              <Text style={sheetStyles.title}>
                {t('asset.files.addSheetTitle')}
              </Text>
              <Text style={sheetStyles.subtitle}>
                {t('asset.files.addSheetSubtitle')}
              </Text>
            </View>
            <Pressable
              onPress={() => handleTypeSelect('file')}
              style={sheetStyles.optionButton}
            >
              <Text style={sheetStyles.optionText}>
                {t('asset.files.uploadFile')}
              </Text>
            </Pressable>
            <Pressable
              onPress={() => handleTypeSelect('link')}
              style={sheetStyles.optionButton}
            >
              <Text style={sheetStyles.optionText}>
                {t('asset.files.addLink')}
              </Text>
            </Pressable>
          </View>
        </View>
      )}
    </>
  );
};

// TODO: Crop Circle Redesign | Remove after feature is fully rolled out
const AddFilesLinksButtonOld = ({
  asset,
  invalidateQuery,
  disabled,
}: Props) => {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [selectedType, setSelectedType] = useState<AddAssetMediaType | null>(null);

  const mediaIdsRef = useRef<Set<string>>(
    new Set((asset?.media || []).map((m) => m.id))
  );

  useEffect(() => {
    mediaIdsRef.current = new Set((asset?.media || []).map((m) => m.id));
  }, [asset?.media]);

  const openMenu = () => setVisible(true);
  const closeMenu = () => setVisible(false);

  const handleTypeSelect = (type: AddAssetMediaType) => {
    setSelectedType(type);
    closeMenu();
  };

  const handleModalComplete = () => {
    setSelectedType(null);
  };

  const onAssetUploadComplete = async (id: string) => {
    mediaIdsRef.current.add(id);
    const media = Array.from(mediaIdsRef.current).map((mid) => ({ id: mid }));
    await updateAsset({ input: { id: asset.id, media } });
    await invalidateQuery();
    handleModalComplete();
  };

  const { mutateAsync: updateAsset } = useUpdateMutation({
    modelName: 'asset',
    query: UPDATE_ASSET,
  });

  return (
    <>
      <Portal>
        <Modal
          visible={!!selectedType}
          onDismiss={handleModalComplete}
          contentContainerStyle={oldStyles.modalContainer}
        >
          {selectedType && (
            <CreateFileForm
              type={selectedType}
              isProductContext
              onProductUploadComplete={onAssetUploadComplete}
              onComplete={handleModalComplete}
            />
          )}
        </Modal>
      </Portal>

      <View style={oldStyles.container}>
        <Menu
          visible={visible}
          onDismiss={closeMenu}
          contentStyle={oldStyles.menuContent}
          anchor={
            <Pressable
              onPress={openMenu}
              style={oldStyles.button}
              disabled={disabled}
            >
              <View style={oldStyles.customButton}>
                <MaterialCommunityIcons
                  name="plus"
                  color={Colors.primaryDark}
                  size={16}
                  style={oldStyles.icon}
                />
                <Text style={oldStyles.buttonLabel}>
                  {t('asset.files.addFilesLinks')}
                </Text>
                <MaterialCommunityIcons
                  name={visible ? 'chevron-up' : 'chevron-down'}
                  color={Colors.primaryDark}
                  size={16}
                  style={oldStyles.chevron}
                />
              </View>
            </Pressable>
          }
        >
          <Menu.Item
            leadingIcon="file-upload"
            onPress={() => handleTypeSelect('file')}
            title="File"
            style={oldStyles.menuItem}
          />
          <Menu.Item
            leadingIcon="link"
            onPress={() => handleTypeSelect('link')}
            title="Link"
            style={oldStyles.menuItem}
          />
        </Menu>
      </View>
    </>
  );
};

const AddFilesLinksButtonBase = ({
  asset,
  invalidateQuery,
  disabled,
  headerMode,
}: Props) => {
  const [visible, setVisible] = useState(false);
  const [selectedType, setSelectedType] = useState<AddAssetMediaType | null>(null);

  const mediaIdsRef = useRef<Set<string>>(
    new Set((asset?.media || []).map((m) => m.id))
  );

  useEffect(() => {
    mediaIdsRef.current = new Set((asset?.media || []).map((m) => m.id));
  }, [asset?.id, asset?.media]);

  const openMenu = () => setVisible(true);
  const closeMenu = () => setVisible(false);

  const handleTypeSelect = (type: AddAssetMediaType) => {
    setSelectedType(type);
    closeMenu();
  };

  const handleModalComplete = () => {
    setSelectedType(null);
  };

  const onAssetUploadComplete = async (id: string) => {
    mediaIdsRef.current.add(id);
    const media = Array.from(mediaIdsRef.current).map((mid) => ({ id: mid }));
    await updateAsset({ input: { id: asset.id, media } });
    await invalidateQuery();
    handleModalComplete();
  };

  const containerRef = useRef<View>(null);

  useEffect(() => {
    if (!visible) return;

    const handleClickOutside = (e: MouseEvent) => {
      const node = containerRef.current as unknown as HTMLElement;
      if (node && !node.contains(e.target as Node)) {
        closeMenu();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [visible]);

  const { mutateAsync: updateAsset } = useUpdateMutation({
    modelName: 'asset',
    query: UPDATE_ASSET,
  });

  return (
    <>
      <Portal>
        <Modal
          visible={!!selectedType}
          onDismiss={handleModalComplete}
          contentContainerStyle={baseStyles.modalContainer}
        >
          {selectedType && (
            <CreateFileForm
              type={selectedType}
              isProductContext
              onProductUploadComplete={onAssetUploadComplete}
              onComplete={handleModalComplete}
            />
          )}
        </Modal>
      </Portal>

      <View
        ref={containerRef}
        style={headerMode ? baseStyles.headerContainer : baseStyles.container}
      >
        {visible && (
          <View
            style={
              headerMode ? baseStyles.headerDropdown : baseStyles.dropdown
            }
          >
            <Button
              onPress={() => handleTypeSelect('file')}
              size="large"
              variant="rectangular"
            >
              <Text style={baseStyles.menuItemText}>File</Text>
            </Button>
            <Button
              onPress={() => handleTypeSelect('link')}
              size="large"
              variant="rectangular"
            >
              <Text style={baseStyles.menuItemText}>Website Link</Text>
            </Button>
          </View>
        )}
        {headerMode ? (
          <AddButton onPress={openMenu} />
        ) : (
          <Button
            onPress={openMenu}
            style={baseStyles.button}
            disabled={disabled}
            size="large"
            variant="rectangular"
          >
            <Text style={baseStyles.buttonLabel}>Add File</Text>
          </Button>
        )}
      </View>
    </>
  );
};

// TODO: Crop Circle Redesign | Remove after feature is fully rolled out
const oldStyles = StyleSheet.create({
  container: {
    marginTop: 30,
  },
  button: {
    height: 44,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: Colors.white,
    borderRadius: 8,
    borderColor: Colors.primaryDark,
    borderWidth: 2,
  },
  buttonLabel: {
    color: Colors.primaryDark,
    fontWeight: '500',
    fontSize: 16,
  },
  icon: {
    marginRight: 4,
  },
  modalContainer: {
    flex: 1,
    alignSelf: 'center',
    justifyContent: 'flex-start',
    alignItems: 'center',
    width: 560,
    maxHeight: 600,
    borderRadius: 12,
    backgroundColor: Colors.primaryGrayBackground,
  },
  customButton: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
  },
  menuContent: {
    backgroundColor: Colors.white,
    flexDirection: 'column',
    flex: 1,
    top: 30,
  },
  chevron: {
    marginLeft: 4,
  },
  menuItem: {
    backgroundColor: Colors.white,
  },
});

const baseStyles = StyleSheet.create({
  container: {
    marginTop: 16,
  },
  button: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  buttonLabel: {
    color: colors.brand.black,
    fontWeight: '500',
    fontSize: 16,
  },
  modalContainer: {
    flex: 1,
    alignSelf: 'center',
    justifyContent: 'flex-start',
    alignItems: 'center',
    width: 560,
    maxHeight: 600,
    borderRadius: 12,
    backgroundColor: colors.neutral[200],
  },
  dropdown: {
    position: 'absolute' as const,
    bottom: '100%',
    left: 0,
    right: 0,
    backgroundColor: colors.brand.fullWhite,
    borderRadius: 8,
    padding: 16,
    gap: 12,
    marginBottom: 8,
    zIndex: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: -2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  headerContainer: {
    position: 'relative' as const,
  },
  headerDropdown: {
    position: 'absolute' as const,
    top: '100%',
    right: 0,
    minWidth: 180,
    backgroundColor: colors.brand.fullWhite,
    borderRadius: 8,
    padding: 16,
    gap: 12,
    marginTop: 8,
    zIndex: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  menuItem: {
    backgroundColor: colors.neutral[200],
    borderRadius: 8,
    height: 48,
    justifyContent: 'center',
    alignItems: 'center',
  },
  menuItemText: {
    color: colors.brand.black,
    fontWeight: '500',
    fontSize: 16,
  },
});

const sheetStyles = StyleSheet.create({
  overlay: {
    position: 'absolute' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    justifyContent: 'flex-end',
    zIndex: 10,
  },
  backdrop: {
    position: 'absolute' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.15)',
  },
  sheet: {
    backgroundColor: colors.brand.fullWhite,
    paddingHorizontal: 24,
    paddingTop: 20,
    paddingBottom: 24,
    gap: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: -2 },
    shadowOpacity: 0.08,
    shadowRadius: 8,
    elevation: 8,
  },
  headerBlock: {
    alignItems: 'center',
    marginBottom: 4,
  },
  title: {
    fontSize: 18,
    fontWeight: '600',
    color: colors.brand.black,
  },
  subtitle: {
    fontSize: 14,
    color: colors.neutral[600],
    marginTop: 2,
  },
  optionButton: {
    backgroundColor: colors.neutral[200],
    borderRadius: 8,
    height: 48,
    justifyContent: 'center',
    alignItems: 'center',
  },
  optionText: {
    fontSize: 16,
    fontWeight: '500',
    color: colors.brand.black,
  },
});

const UPDATE_ASSET = `
  mutation UpdateAsset($input: MutateAssetInput!) {
    updateAsset(input: $input) {
      id
    }
  }
`;
